/**
 * Customers Overview Hook
 *
 * Custom React Query hook for fetching global customer analytics.
 * Provides company-wide customer metrics, top customers, and distribution.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import { SalesAnalyticsService } from "../services/sales-analytics";
import type { CustomersOverviewData } from "@/shared/types/sales-analytics";

/**
 * Hook to fetch global customers overview
 *
 * @param params - Optional period, limit, and preview parameters
 * @param options - React Query options
 *
 * @example
 * const { data, isLoading } = useCustomersOverview({
 *   period: "2025-09",
 *   limit: 50,
 *   previewAs: "salespersonId" // Admin preview mode
 * });
 */
export function useCustomersOverview(
  params?: {
    period?: string;
    limit?: number;
    previewAs?: string; // Admin preview mode
  },
  options?: Omit<UseQueryOptions<CustomersOverviewData>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new SalesAnalyticsService(api);

  return useQuery<CustomersOverviewData>({
    queryKey: ['customersOverview', params],
    queryFn: () => service.getCustomersOverview(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

/**
 * Helper to get current period in YYYY-MM format
 */
export function useCurrentPeriod() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // JavaScript months are 0-indexed

  return `${year}-${String(month).padStart(2, '0')}`;
}
